import { useQuery } from 'react-query';
import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import { useMemo } from 'react';
import pathfinder from '@fiverr-private/pathfinder';
import { SectionTypes } from '@fiverr-private/ai_brief';
import { getContext } from '@fiverr-private/fiverr_context';
import { addCustomSections } from '../../components/Sections/useCustomSections';
import { SUGGESTED_QUESTION_TRANSLATION_KEYS } from '../../components/Sections/ScreeningQuestions';
import { appTranslate } from '../../i18n';
import { BriefClassificationResponse } from '../../api';
import { seedSectionsFromApi } from './utils/seedSectionsFromApi';
import { transformFreelancerPreferences } from './utils/transformFreelancerPreferences';
import { transformApiAttachments } from './utils/transformAttachments';
import { getScreeningQuestionsSeed } from './utils/getScreeningQuestionsSeed';
import { BuyerBriefOverviewResponse, SectionsApiResponse } from './types';

export interface UseEnrichedBuyerBriefParams {
  briefId: string;
  enabled?: boolean;
  onError?: (error: Error) => void;
}

export const useEnrichedBuyerBrief = ({ briefId, enabled = true, onError }: UseEnrichedBuyerBriefParams) => {
  const { locale } = getContext();
  const suggestedTextToKeyMap = useMemo(
    () => new Map(SUGGESTED_QUESTION_TRANSLATION_KEYS.map((key) => [appTranslate<string>(key, { locale }), key])),
    [locale]
  );
  return useQuery(
    ['EnrichedBuyerBrief', briefId],
    async () => {
      const { data: brief } = await axios.get<BuyerBriefOverviewResponse>(
        pathfinder('get_dm_brief_buyer_overview', { briefId })
      );

      if (!brief) {
        throw new Error('Brief data not found');
      }

      const { data: sectionApi } = await axios.post<SectionsApiResponse>(pathfinder('get_brief_sections'), {
        category_id: brief.categoryId,
        sub_category_id: brief.subCategoryId ?? 0,
        nested_sub_category_id: brief.nestedSubCategoryId ?? brief.subCategoryId ?? 0,
      });

      if (!sectionApi?.sections) {
        throw new Error('Sections API data not found');
      }

      const seededSections = seedSectionsFromApi(brief, sectionApi);

      const screeningQuestionsSeed = getScreeningQuestionsSeed(brief, suggestedTextToKeyMap);

      const briefCopy = {
        ...cloneDeep(brief),
        sections: [...seededSections, ...screeningQuestionsSeed],
      };

      const classification: BriefClassificationResponse = {
        context_id: brief.contextId ?? '',
        leaf_classification: [
          {
            category_id: brief.categoryId ?? 0,
            sub_category_id: brief.subCategoryId ?? 0,
            nested_sub_category_id: brief.nestedSubCategoryId ?? brief.subCategoryId ?? 0,
            display_name: brief.categoryName ?? brief.title ?? '',
            explainer: brief.description ?? '',
          },
        ],
        static_data: {
          ...sectionApi,
          budget: sectionApi.budget_config,
        },
        resolved_data: {
          need_type: {
            needType: brief.needType ?? undefined,
            ongoingWorkPotential: brief.needTypeDetails?.ongoingWorkPotential ?? false,
          },

          budget: {
            min_price_in_cents: brief.budgetRangeInCents?.min ?? brief.budgetInCents,
            max_price_in_cents: brief.budgetRangeInCents?.max ?? brief.budgetInCents,
            pricing_type: brief.pricingType,
          },
          delivery_time: {
            timeline: brief.timeline,
            delivery_date: brief.deliveryDate ? String(brief.deliveryDate) : '',
            timeline_details: brief.timelineDetails,
          },
          metadata: {
            filters:
              brief.metadataAnswers?.serviceOptions?.reduce((acc, opt) => {
                acc[opt.key] = opt.values;
                return acc;
              }, {} as Record<string, string[]>) ?? {},
          },
          freelancer_preferences: transformFreelancerPreferences(brief.metadataAnswers?.freelancerPreferences),
          query_string: '',
        },
      };

      const enrichedBrief = addCustomSections({
        brief: briefCopy as Parameters<typeof addCustomSections>[0]['brief'],
        additionalData: classification as Parameters<typeof addCustomSections>[0]['additionalData'],
        enableEnhancedUx: true,
        enableScreeningQuestions: true,
        rollouts: { inMustangsPersonBrief: true },
      });

      const attachmentsSection = enrichedBrief?.sections?.find((s) => s.type === SectionTypes.attachments);
      if (attachmentsSection && brief.attachments?.length) {
        const transformedAttachments = transformApiAttachments(brief.attachments);
        attachmentsSection.attachments = transformedAttachments;
        attachmentsSection.included = true;
      }

      return {
        brief: enrichedBrief,
        extraWaveStatus: brief.extraWaveStatus,
        briefClassification: classification,
      };
    },
    {
      enabled,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error: Error) => {
        onError?.(error);
      },
    }
  );
};
